import { useState, useEffect, useCallback } from "react";
import { Upload, Trash2 } from "lucide-react";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";
import {
  FiEdit2,
  FiEye,
  FiFilter,
  FiPlus,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useServices } from "../useHook";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import { IoEye } from "react-icons/io5";
import { MdDelete } from "react-icons/md";
import { sanitizePositionInput } from "../../../Utils/positionInput";
import { UrduInput } from "../../../Components/Shared/UrduInput";
import {
  ImageSeoFields,
  appendImageSeoToFormData,
  appendPrefixedImageSeoToFormData,
} from "../../../Components/Shared/ImageSeoFields";
const REQUIRED_WIDTH = 450;
const REQUIRED_HEIGHT = 450;
const MAIN_IMAGE_WIDTH = 1200;
const MAIN_IMAGE_HEIGHT = 900;
const GALLERY_IMAGE_WIDTH = 1200;
const GALLERY_IMAGE_HEIGHT = 900;

const checkImageDimension = (file, requiredWidth, requiredHeight) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = function () {
      if (this.width === requiredWidth && this.height === requiredHeight) {
        resolve(true);
      } else {
        resolve(false);
      }
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

const Services = () => {
  const navigate = useNavigate();
  const {
    getServices,
    addService,
    updateService,
    deleteService,
    searchServices,
    getServiceCategoriesForSelect,
  } = useServices();

  const { hasPermission } = usePermission();
  const canRead = hasPermission(SystemPermissions.Services.READ);
  const canCreate = hasPermission(SystemPermissions.Services.CREATE);
  const canUpdate = hasPermission(SystemPermissions.Services.UPDATE);
  const canDelete = hasPermission(SystemPermissions.Services.DELETE);
  const canShowActions = canRead || canUpdate || canDelete;

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serviceData, setServiceData] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [isCatLoading, setIsCatLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    title_ur: "",
    customUrl: "",
    description: "",
    description_ur: "",
    categoryId: "",
    city: "",
    city_ur: "",
    image: null,
    imagePreview: null,
    image_alt: "",
    image_title: "",
    thumbnail_image: null,
    thumbnailPreview: null,
    thumbnail_image_alt: "",
    thumbnail_image_title: "",
    video: null,
    videoLabel: "",
    videoPreview: null,
    galleryFiles: [],
    galleryPositions: [],
    galleryImageAlts: [],
    galleryImageTitles: [],
  });
  const [existingGallery, setExistingGallery] = useState([]); // for edit: images to keep [{ id, image_url }]

  const loadServices = useCallback(
    async (
      currentOffset = offset,
      currentLimit = limit,
      currentSearchFilter = searchFilter,
    ) => {
      setIsLoading(true);
      try {
        await getServices(
          setServiceData,
          setTotal,
          currentOffset,
          currentLimit,
          currentSearchFilter,
        );
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load services. Please refresh the page.",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [offset, limit, searchFilter, getServices],
  );

  useEffect(() => {
    setIsCatLoading(true);
    getServiceCategoriesForSelect(setServiceCategories)
      .catch((error) => {
        console.error("Error fetching service categories:", error);
      })
      .finally(() => {
        setIsCatLoading(false);
      });
  }, [getServiceCategoriesForSelect]);

  useEffect(() => {
    loadServices(offset, limit);
  }, [offset, limit, loadServices]);

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
    loadServices(newOffset, limit);
  };

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setOffset(0);
      const response = await searchServices(searchFilter, 0, limit);
      setServiceData(response.data ?? []);
      const totalRecords =
        response.pagination?.totalRecords ?? response.data?.length ?? 0;
      setTotal(totalRecords);
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchFilter("");
    setOffset(0);
    loadServices(0, limit, "");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleQuillChange = (value) => {
    setFormData({ ...formData, description: value });
  };

  const handleQuillChangeUr = (value) => {
    setFormData((prev) => ({ ...prev, description_ur: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const isValid = await checkImageDimension(
        file,
        MAIN_IMAGE_WIDTH,
        MAIN_IMAGE_HEIGHT,
      );
      if (!isValid) {
        Swal.fire({
          icon: "error",
          title: "Dimension Error",
          text: `Main image dimensions must be exactly ${MAIN_IMAGE_WIDTH}x${MAIN_IMAGE_HEIGHT} pixels.`,
        });
        e.target.value = "";
        return;
      }
      setFormData({
        ...formData,
        image: file,
        imagePreview: URL.createObjectURL(file),
      });
    }
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const isValid = await checkImageDimension(
        file,
        REQUIRED_WIDTH,
        REQUIRED_HEIGHT,
      );
      if (!isValid) {
        Swal.fire({
          icon: "error",
          title: "Dimension Error",
          text: `Thumbnail dimensions must be exactly ${REQUIRED_WIDTH}x${REQUIRED_HEIGHT} pixels.`,
        });
        e.target.value = "";
        return;
      }
      setFormData({
        ...formData,
        thumbnail_image: file,
        thumbnailPreview: URL.createObjectURL(file),
      });
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({
        ...formData,
        video: file,
        videoLabel: file.name,
        videoPreview: URL.createObjectURL(file),
      });
    }
  };

  const removeImage = () => {
    if (formData.imagePreview && formData.imagePreview.startsWith("blob:"))
      URL.revokeObjectURL(formData.imagePreview);
    setFormData({
      ...formData,
      image: null,
      imagePreview: null,
    });
  };

  const removeThumbnail = () => {
    if (
      formData.thumbnailPreview &&
      formData.thumbnailPreview.startsWith("blob:")
    )
      URL.revokeObjectURL(formData.thumbnailPreview);
    setFormData({
      ...formData,
      thumbnail_image: null,
      thumbnailPreview: null,
    });
  };

  const removeVideo = () => {
    if (formData.videoPreview && formData.videoPreview.startsWith("blob:"))
      URL.revokeObjectURL(formData.videoPreview);
    setFormData({
      ...formData,
      video: null,
      videoLabel: "",
      videoPreview: null,
    });
  };

  const MAX_GALLERY = 5;

  const addGalleryFile = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const current = formData.galleryFiles;
    const existingCount = isEditing ? existingGallery.length : 0;
    const remaining = MAX_GALLERY - existingCount - current.length;
    const toAdd = files.slice(0, remaining);

    if (toAdd.length) {
      const validFiles = [];
      for (const file of toAdd) {
        const isValid = await checkImageDimension(
          file,
          GALLERY_IMAGE_WIDTH,
          GALLERY_IMAGE_HEIGHT,
        );
        if (isValid) {
          validFiles.push(file);
        } else {
          Swal.fire({
            icon: "error",
            title: "Dimension Error",
            text: `Gallery image "${file.name}" dimensions must be exactly ${GALLERY_IMAGE_WIDTH}x${GALLERY_IMAGE_HEIGHT} pixels.`,
          });
        }
      }

      if (validFiles.length) {
        const startPosition =
          (isEditing ? existingGallery.length : 0) + current.length + 1;
        setFormData({
          ...formData,
          galleryFiles: [...current, ...validFiles],
          galleryPositions: [
            ...(formData.galleryPositions || []),
            ...validFiles.map((_, i) => String(startPosition + i)),
          ],
          galleryImageAlts: [
            ...(formData.galleryImageAlts || []),
            ...validFiles.map(() => ""),
          ],
          galleryImageTitles: [
            ...(formData.galleryImageTitles || []),
            ...validFiles.map(() => ""),
          ],
        });
      }
    }
    e.target.value = "";
  };

  const removeGalleryFile = (index) => {
    const next = formData.galleryFiles.filter((_, i) => i !== index);
    const nextPositions = (formData.galleryPositions || []).filter(
      (_, i) => i !== index,
    );
    const nextAlts = (formData.galleryImageAlts || []).filter(
      (_, i) => i !== index,
    );
    const nextTitles = (formData.galleryImageTitles || []).filter(
      (_, i) => i !== index,
    );
    setFormData({
      ...formData,
      galleryFiles: next,
      galleryPositions: nextPositions,
      galleryImageAlts: nextAlts,
      galleryImageTitles: nextTitles,
    });
  };

  const onExistingGalleryPositionChange = (index, value) => {
    const cleaned = sanitizePositionInput(value);
    setExistingGallery((prev) =>
      prev.map((img, i) => (i === index ? { ...img, position: cleaned } : img)),
    );
  };

  const onNewGalleryPositionChange = (index, value) => {
    const cleaned = sanitizePositionInput(value);
    setFormData((prev) => {
      const positions = [...(prev.galleryPositions || [])];
      positions[index] = cleaned;
      return { ...prev, galleryPositions: positions };
    });
  };

  const onExistingGallerySeoChange = (index, field, value) => {
    setExistingGallery((prev) =>
      prev.map((img, i) => (i === index ? { ...img, [field]: value } : img)),
    );
  };

  const onNewGallerySeoChange = (index, field, value) => {
    const key = field === "image_alt" ? "galleryImageAlts" : "galleryImageTitles";
    setFormData((prev) => {
      const next = [...(prev[key] || [])];
      next[index] = value;
      return { ...prev, [key]: next };
    });
  };

  const removeExistingGalleryImage = (index) => {
    setExistingGallery((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    if (formData.imagePreview && formData.imagePreview.startsWith("blob:"))
      URL.revokeObjectURL(formData.imagePreview);
    if (formData.videoPreview && formData.videoPreview.startsWith("blob:"))
      URL.revokeObjectURL(formData.videoPreview);
    setFormData({
      title: "",
      title_ur: "",
      customUrl: "",
      description: "",
      description_ur: "",
      categoryId: "",
      city: "",
      city_ur: "",
      image: null,
      imagePreview: null,
      image_alt: "",
      image_title: "",
      thumbnail_image: null,
      thumbnailPreview: null,
      thumbnail_image_alt: "",
      thumbnail_image_title: "",
      video: null,
      videoLabel: "",
      videoPreview: null,
      galleryFiles: [],
      galleryPositions: [],
      galleryImageAlts: [],
      galleryImageTitles: [],
    });
    setExistingGallery([]);
    setEditId(null);
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Title is required.",
      });
      return;
    }
    if (!formData.customUrl?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL is required.",
      });
      return;
    }
    if (!formData.description?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Description is required.",
      });
      return;
    }
    if (!formData.categoryId) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Category is required.",
      });
      return;
    }
    if (!isEditing) {
      if (!formData.image) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Main image is required.",
        });
        return;
      }
      if (!formData.thumbnail_image) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Thumbnail image is required.",
        });
        return;
      }
      if (!formData.video) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Video is required.",
        });
        return;
      }
    }
    setIsSubmitting(true);
    try {
      const apiData = new FormData();
      apiData.append("title", formData.title.trim());
      if (formData.title_ur?.trim())
        apiData.append("title_ur", formData.title_ur.trim());
      if (formData.customUrl?.trim())
        apiData.append("customUrl", formData.customUrl.trim());
      if (formData.description)
        apiData.append("description", formData.description);
      if (formData.description_ur?.trim())
        apiData.append("description_ur", formData.description_ur.trim());
      // Optional category: send as empty string when not selected
      apiData.append("categoryId", formData.categoryId ?? "");
      apiData.append("city", formData.city || "");
      if (formData.city_ur?.trim())
        apiData.append("city_ur", formData.city_ur.trim());
      if (formData.image) apiData.append("image", formData.image);
      appendImageSeoToFormData(apiData, formData.image_alt, formData.image_title);
      if (formData.thumbnail_image)
        apiData.append("thumbnail_image", formData.thumbnail_image);
      appendPrefixedImageSeoToFormData(
        apiData,
        "thumbnail_image",
        formData.thumbnail_image_alt,
        formData.thumbnail_image_title,
      );
      if (formData.video) apiData.append("video", formData.video);

      if (isEditing) {
        if (existingGallery.length > 0) {
          existingGallery.forEach((image) => {
            apiData.append(
              "existing_images",
              JSON.stringify({
                id: image.id,
                image_url: image.image_url,
                position: image.position ?? "",
                image_alt: image.image_alt || "",
                image_title: image.image_title || "",
              }),
            );
          });
        } else {
          apiData.append("existing_images", "[]");
        }
        formData.galleryFiles.forEach((file, index) => {
          apiData.append("images", file);
          const position =
            formData.galleryPositions?.[index] != null &&
            formData.galleryPositions[index] !== ""
              ? formData.galleryPositions[index]
              : existingGallery.length + index + 1;
          apiData.append("imagePosition", position);
          const alt = formData.galleryImageAlts?.[index];
          const title = formData.galleryImageTitles?.[index];
          if (alt != null && String(alt).trim()) {
            apiData.append("imageAlt", String(alt).trim());
          }
          if (title != null && String(title).trim()) {
            apiData.append("imageTitle", String(title).trim());
          }
        });
      } else {
        formData.galleryFiles.forEach((file, index) => {
          apiData.append("images", file);
          const position =
            formData.galleryPositions?.[index] != null &&
            formData.galleryPositions[index] !== ""
              ? formData.galleryPositions[index]
              : index + 1;
          apiData.append("imagePosition", position);
          const alt = formData.galleryImageAlts?.[index];
          const title = formData.galleryImageTitles?.[index];
          if (alt != null && String(alt).trim()) {
            apiData.append("imageAlt", String(alt).trim());
          }
          if (title != null && String(title).trim()) {
            apiData.append("imageTitle", String(title).trim());
          }
        });
      }

      if (isEditing) {
        await updateService(editId, apiData);
      } else {
        await addService(apiData);
      }

      await loadServices(offset, limit);
      setShowModal(false);
      resetForm();
      Swal.fire({
        icon: "success",
        title: isEditing ? "Service Updated" : "Service Added",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save service. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (service) => {
    setEditId(service.id);
    setIsEditing(true);
    const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
    const imagePreview = service.image
      ? service.image.startsWith("http")
        ? service.image
        : `${baseUrl}${service.image.startsWith("/") ? "" : "/"}${service.image}`
      : null;
    const videoPreview = service.video
      ? service.video.startsWith("http")
        ? service.video
        : `${baseUrl}${service.video.startsWith("/") ? "" : "/"}${service.video}`
      : null;
    const customUrl = service.custom_url ?? service.customUrl ?? "";
    setFormData({
      title: service.title || "",
      title_ur: service.title_ur || "",
      customUrl: customUrl.replace(/\/$/, ""),
      description: service.description || "",
      description_ur: service.description_ur || "",
      categoryId:
        service.category_id !== undefined && service.category_id !== null
          ? String(service.category_id)
          : (service.categoryId ?? ""),
      city: service.city || service.City || service.city_name || "",
      city_ur: service.city_ur || "",
      image: null,
      imagePreview,
      image_alt: service.image_alt || "",
      image_title: service.image_title || "",
      thumbnail_image: null,
      thumbnailPreview: service.thumbnail_image
        ? service.thumbnail_image.startsWith("http")
          ? service.thumbnail_image
          : `${baseUrl}${service.thumbnail_image.startsWith("/") ? "" : "/"}${service.thumbnail_image}`
        : null,
      thumbnail_image_alt: service.thumbnail_image_alt || "",
      thumbnail_image_title: service.thumbnail_image_title || "",
      video: null,
      videoLabel: "",
      videoPreview,
      galleryFiles: [],
      galleryPositions: [],
      galleryImageAlts: [],
      galleryImageTitles: [],
    });
    setExistingGallery(
      Array.isArray(service.images)
        ? service.images
            .slice()
            .sort(
              (a, b) =>
                (Number(a.position) || 0) - (Number(b.position) || 0) ||
                String(a.id).localeCompare(String(b.id)),
            )
            .map((img) => ({
              ...img,
              position:
                img.position != null && Number(img.position) >= 1
                  ? String(img.position)
                  : "",
            }))
        : [],
    );
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await deleteService(id);
        await loadServices(offset, limit);
        Swal.fire("Deleted!", "Service deleted successfully.", "success");
      } catch (error) {
        Swal.fire(
          "Error!",
          "Failed to delete service. Please try again.",
          "error",
        );
      }
    }
  };

  const handlePageView = (id) => {
    navigate(`/services/${id}`);
  };

  useEffect(() => {
    return () => {
      if (formData.imagePreview && formData.imagePreview.startsWith("blob:"))
        URL.revokeObjectURL(formData.imagePreview);
      if (
        formData.thumbnailPreview &&
        formData.thumbnailPreview.startsWith("blob:")
      )
        URL.revokeObjectURL(formData.thumbnailPreview);
      if (formData.videoPreview && formData.videoPreview.startsWith("blob:"))
        URL.revokeObjectURL(formData.videoPreview);
    };
  }, [formData.imagePreview, formData.thumbnailPreview, formData.videoPreview]);

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Services</h2>

        {canCreate && (
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn btn-primary h-[44px]"
          >
            <FiPlus className="text-white" />
            Add Service
          </button>
        )}
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by title"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Title</th>
                {canShowActions && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {serviceData.length > 0 ? (
                serviceData.map((service, index) => (
                  <tr
                    key={service.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">{offset + index + 1}</td>
                    <td className="table-td">{service.title}</td>

                    {canShowActions && (
                      <td className="table-td">
                        <div className="flex space-x-2">
                          {canRead && (
                            <button
                              className="btn btn-view "
                              onClick={() => handlePageView(service.id)}
                            >
                              <IoEye className="text-white" /> View
                            </button>
                          )}
                          {canUpdate && (
                            <button
                              className="btn btn-edit text-white"
                              onClick={() => handleEdit(service)}
                            >
                              <FiEdit2 className="text-white" /> Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn btn-delete text-white"
                              onClick={() => handleDelete(service.id)}
                            >
                              <MdDelete className="text-white" /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={canShowActions ? 3 : 2}
                    className="py-4 px-4 text-center text-gray-500 font-bold"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Loading services...
                      </div>
                    ) : (
                      "No services found"
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {serviceData.length > 0 && total > limit && (
        <div className="mt-4">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            className="bg-white"
          />
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                {isEditing ? "Edit Service" : "Add Service"}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
                <UrduInput
                  label="عنوان"
                  name="title_ur"
                  value={formData.title_ur}
                  onChange={handleChange}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="customUrl"
                  value={formData.customUrl}
                  onChange={handleChange}
                  placeholder="e.g. consultation or /services/consultation"
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="e.g. New York"
                  className="w-full border border-gray-300 rounded-md p-2"
                />
                <UrduInput
                  label="شہر"
                  name="city_ur"
                  value={formData.city_ur}
                  onChange={handleChange}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                >
                  <option value="">Select category</option>
                  {Array.isArray(serviceCategories) &&
                  serviceCategories.length > 0 ? (
                    serviceCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      {isCatLoading ? "Loading..." : "No categories added yet"}
                    </option>
                  )}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <div className="custom-quill-editor border border-gray-300 rounded-md overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={handleQuillChange}
                  />
                </div>
                <label className="block text-sm font-medium text-indigo-700 text-right mt-10 mb-1">
                  تفصیل
                </label>
                <div className="quill-ur-service border border-indigo-200 rounded-md mb-16">
                  <style>{`.quill-ur-service .ql-editor { direction: rtl; text-align: right; }`}</style>
                  <ReactQuill
                    theme="snow"
                    value={formData.description_ur}
                    onChange={handleQuillChangeUr}
                    className="h-40"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Main image
                  {!isEditing && <span className="text-red-500">*</span>}
                  {/* {isEditing && " (optional, leave empty to keep current)"} */}
                </label>
                <div className="border border-dashed border-gray-300 p-4 rounded-md">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="service-image"
                  />
                  {!formData.imagePreview && (
                    <label
                      htmlFor="service-image"
                      className="flex flex-col items-center justify-center cursor-pointer"
                    >
                      <Upload size={24} className="text-gray-500 mb-2" />
                      <span className="text-sm text-gray-500">
                        Click to upload image
                      </span>
                      <span className="text-[10px] text-gray-400 mt-1">
                        Required size: {MAIN_IMAGE_WIDTH}x{MAIN_IMAGE_HEIGHT}{" "}
                        pixels
                      </span>
                    </label>
                  )}
                  {(formData.imagePreview || formData.image) && (
                    <div className="mt-2 relative inline-block">
                      <img
                        src={
                          formData.imagePreview ||
                          (formData.image &&
                            URL.createObjectURL(formData.image))
                        }
                        alt="Preview"
                        className="h-24 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                  <ImageSeoFields
                    imageAlt={formData.image_alt}
                    imageTitle={formData.image_title}
                    onAltChange={(value) =>
                      setFormData({ ...formData, image_alt: value })
                    }
                    onTitleChange={(value) =>
                      setFormData({ ...formData, image_title: value })
                    }
                    className="mt-3"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Thumbnail image (Required size: {REQUIRED_WIDTH}x
                  {REQUIRED_HEIGHT}px)
                  {!isEditing && <span className="text-red-500">*</span>}
                  {/* {isEditing && " (optional, leave empty to keep current)"} */}
                </label>
                <div className="border border-dashed border-gray-300 p-4 rounded-md">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailChange}
                    className="hidden"
                    id="service-thumbnail"
                  />
                  {!formData.thumbnailPreview && (
                    <label
                      htmlFor="service-thumbnail"
                      className="flex flex-col items-center justify-center cursor-pointer"
                    >
                      <Upload size={24} className="text-gray-500 mb-2" />
                      <span className="text-sm text-gray-500">
                        Click to upload thumbnail
                      </span>
                      <span className="text-[10px] text-gray-400 mt-1">
                        Required size: {REQUIRED_WIDTH}x{REQUIRED_HEIGHT} pixels
                      </span>
                    </label>
                  )}
                  {(formData.thumbnailPreview || formData.thumbnail_image) && (
                    <div className="mt-2 relative inline-block">
                      <img
                        src={
                          formData.thumbnailPreview ||
                          (formData.thumbnail_image &&
                            URL.createObjectURL(formData.thumbnail_image))
                        }
                        alt="Preview"
                        className="h-24 w-24 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={removeThumbnail}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                  <ImageSeoFields
                    imageAlt={formData.thumbnail_image_alt}
                    imageTitle={formData.thumbnail_image_title}
                    onAltChange={(value) =>
                      setFormData({ ...formData, thumbnail_image_alt: value })
                    }
                    onTitleChange={(value) =>
                      setFormData({
                        ...formData,
                        thumbnail_image_title: value,
                      })
                    }
                    className="mt-3"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Video {!isEditing && <span className="text-red-500">*</span>}
                  {/* {isEditing && " (optional, leave empty to keep current)"} */}
                </label>
                <div className="border border-dashed border-gray-300 p-4 rounded-md">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoChange}
                    className="hidden"
                    id="service-video"
                  />
                  {!formData.videoPreview && !formData.videoLabel && (
                    <label
                      htmlFor="service-video"
                      className="flex flex-col items-center justify-center cursor-pointer"
                    >
                      <Upload size={24} className="text-gray-500 mb-2" />
                      <span className="text-sm text-gray-500">
                        Click to upload video
                      </span>
                    </label>
                  )}
                  {(formData.videoPreview || formData.videoLabel) && (
                    <div className="mt-2 text-center">
                      {formData.videoPreview && (
                        <div className="relative inline-block">
                          <video
                            src={formData.videoPreview}
                            className="h-32 rounded border"
                            controls
                          />
                          <button
                            type="button"
                            onClick={removeVideo}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      )}
                      {!formData.videoPreview && formData.videoLabel && (
                        <p className="mt-1 text-sm text-gray-600">
                          {formData.videoLabel}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gallery images
                </label>
                <div className="border border-dashed border-gray-300 p-4 rounded-md">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={addGalleryFile}
                    className="hidden"
                    id="service-gallery"
                  />
                  {existingGallery.length === 0 &&
                  formData.galleryFiles.length === 0 ? (
                    <label
                      htmlFor="service-gallery"
                      className="flex flex-col items-center justify-center cursor-pointer"
                    >
                      <Upload size={24} className="text-gray-500 mb-2" />
                      <span className="text-sm text-gray-500">
                        Click to upload gallery images (max {MAX_GALLERY})
                      </span>
                      <span className="text-[10px] text-gray-400 mt-1">
                        Required size: {GALLERY_IMAGE_WIDTH}x
                        {GALLERY_IMAGE_HEIGHT} pixels
                      </span>
                    </label>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-4 items-center">
                      {isEditing &&
                        existingGallery.map((img, idx) => {
                          const url =
                            typeof img === "object" ? img.image_url : img;
                          const fullUrl = url?.startsWith("http")
                            ? url
                            : `${(process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "")}${url?.startsWith("/") ? "" : "/"}${url}`;
                          return (
                            <div
                              key={`existing-${img.id || idx}`}
                              className="relative inline-block"
                            >
                              <img
                                src={fullUrl}
                                alt="Gallery Preview"
                                className="h-24 w-auto object-cover rounded border"
                              />
                              <button
                                type="button"
                                onClick={() => removeExistingGalleryImage(idx)}
                                className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                              >
                                <Trash2 size={12} />
                              </button>
                              <div className="mt-1">
                                <label className="block text-[10px] text-gray-600 mb-0.5">
                                  Position
                                </label>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[1-9][0-9]*"
                                  value={img.position ?? ""}
                                  onChange={(e) =>
                                    onExistingGalleryPositionChange(
                                      idx,
                                      e.target.value,
                                    )
                                  }
                                  className="w-20 px-1 py-0.5 text-xs border border-gray-300 rounded"
                                  placeholder="1"
                                />
                              </div>
                              <ImageSeoFields
                                stacked
                                imageAlt={img.image_alt || ""}
                                imageTitle={img.image_title || ""}
                                onAltChange={(value) =>
                                  onExistingGallerySeoChange(
                                    idx,
                                    "image_alt",
                                    value,
                                  )
                                }
                                onTitleChange={(value) =>
                                  onExistingGallerySeoChange(
                                    idx,
                                    "image_title",
                                    value,
                                  )
                                }
                                className="mt-2"
                              />
                            </div>
                          );
                        })}

                      {formData.galleryFiles.map((file, idx) => (
                        <div
                          key={`new-${idx}`}
                          className="relative inline-block"
                        >
                          <img
                            src={URL.createObjectURL(file)}
                            alt="Gallery Preview"
                            className="h-24 w-auto object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => removeGalleryFile(idx)}
                            className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                          >
                            <Trash2 size={12} />
                          </button>
                          <div className="mt-1">
                            <label className="block text-[10px] text-gray-600 mb-0.5">
                              Position
                            </label>
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[1-9][0-9]*"
                              value={formData.galleryPositions?.[idx] ?? ""}
                              onChange={(e) =>
                                onNewGalleryPositionChange(idx, e.target.value)
                              }
                              className="w-20 px-1 py-0.5 text-xs border border-gray-300 rounded"
                              placeholder="1"
                            />
                          </div>
                          <ImageSeoFields
                            stacked
                            imageAlt={formData.galleryImageAlts?.[idx] || ""}
                            imageTitle={formData.galleryImageTitles?.[idx] || ""}
                            onAltChange={(value) =>
                              onNewGallerySeoChange(idx, "image_alt", value)
                            }
                            onTitleChange={(value) =>
                              onNewGallerySeoChange(idx, "image_title", value)
                            }
                            className="mt-2"
                          />
                        </div>
                      ))}

                      {(isEditing ? existingGallery.length : 0) +
                        formData.galleryFiles.length <
                        MAX_GALLERY && (
                        <label
                          htmlFor="service-gallery"
                          className="flex flex-col items-center justify-center cursor-pointer h-24 w-24 border border-dashed border-gray-300 rounded hover:bg-gray-50 flex-shrink-0"
                        >
                          <Upload size={20} className="text-gray-500 mb-1" />
                          <span className="text-[10px] text-gray-500 text-center px-1">
                            Add Image
                          </span>
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="btn btn-reset-filter justify-center"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`btn justify-center ${
                    isEditing ? "btn-edit text-white" : "btn-primary"
                  } ${isSubmitting ? "opacity-75 cursor-not-allowed" : ""}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Processing..."
                    : isEditing
                      ? "Update"
                      : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
